<template>
  <div class="be-faq" v-if="hasFaq">
    <h2 class="be-faq__title mb-4">Frequently Asked Questions</h2>

    <div class="be-faq__list">
      <button
        type="button"
        class="be-faq__row"
        v-for="faq in visibleFaqList"
        :key="faq.id"
        @click="openFaq(faq)">
        <span class="be-faq__question">{{ getLocalizedText(faq, 'question') }}</span>
        <ChevronRight :size="18" class="be-faq__chevron" />
      </button>
    </div>

    <b-modal v-model="isModalOpen" centered hide-header hide-footer body-class="p-0">
      <div class="be-faq-modal" v-if="activeFaq">
        <div class="be-faq-modal__head">
          <p class="be-faq-modal__title">{{ getLocalizedText(activeFaq, 'question') }}</p>
          <button type="button" class="be-faq-modal__close" @click="closeFaq">
            <X :size="18" />
          </button>
        </div>
        <div class="be-faq-modal__body">
          <div class="be-faq-modal__answer" v-html="getLocalizedText(activeFaq, 'answer')"></div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { ChevronRight, X } from 'lucide-vue';
import { mapGetters } from 'vuex';
import { get } from '@/services/index.js';

const LANGUAGE_FIELD_SUFFIX = {
  en: 'en',
  id: 'ind',
  de: 'de',
  ru: 'ru',
  zh: 'zh',
  ja: 'ja',
};

export default {
  name: 'BeFaqSection',
  components: {
    ChevronRight,
    X,
  },
  data() {
    return {
      isLoading: true,
      faqList: [],
      activeFaq: null,
    };
  },
  computed: {
    ...mapGetters(['getMerchant', 'getCurrentLanguage']),
    visibleFaqList() {
      return this.faqList
        .filter((faq) => faq.is_active)
        .sort((first, second) => first.position - second.position);
    },
    hasFaq() {
      return !this.isLoading && this.visibleFaqList.length > 0;
    },
    isModalOpen: {
      get() {
        return this.activeFaq !== null;
      },
      set(isShown) {
        if (!isShown) this.activeFaq = null;
      },
    },
  },
  mounted() {
    this.loadFaqList();
  },
  methods: {
    getLocalizedText(faq, field) {
      const suffix = LANGUAGE_FIELD_SUFFIX[this.getCurrentLanguage] || 'en';
      const localizedValue = faq[field + '_' + suffix];
      return this.$isNotEmptyV2(localizedValue) ? localizedValue : faq[field];
    },
    openFaq(faq) {
      this.activeFaq = faq;
    },
    closeFaq() {
      this.activeFaq = null;
    },
    async loadFaqList() {
      try {
        this.isLoading = true;
        const apiParams = { merchant_id: this.getMerchant.id };
        const apiresponse = await get('CORE_HOTEL', 'merchant-faq', apiParams);
        if (!apiresponse.data.success) throw new Error(apiresponse.data.message);

        this.faqList = apiresponse.data.data;
      } catch (error) {
        console.log('ERROR in loadFaqList() (BeFaqSection.vue) : ', error);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.be-faq__title {
  font-family: var(--be-v3-title-font, BeV3Sans), sans-serif;
  font-weight: 600;
  color: #1a1a2e;
  font-size: 22px;
}
.be-faq__list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 48px;
}
@media (max-width: 991.98px) {
  .be-faq__list {
    grid-template-columns: 1fr;
  }
}
.be-faq__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 16px 0;
  background: none;
  border: none;
  border-top: 1px solid #e8e8e8;
  text-align: left;
  cursor: pointer;
}
.be-faq__question {
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif;
  font-weight: 700;
  color: #1a1a2e;
  font-size: 15px;
}
.be-faq__chevron {
  flex-shrink: 0;
  color: #999;
  transition: color 0.2s ease;
}
.be-faq__row:hover .be-faq__chevron {
  color: var(--merchant-primary-color, #79a33a);
}

/* modal is portaled to <body>, outside .be-landing — restate the font family */
.be-faq-modal {
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif;
}
.be-faq-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 24px;
  border-bottom: 1px solid #e8e8e8;
}
.be-faq-modal__title {
  margin: 0;
  font-family: var(--be-v3-title-font, BeV3Sans), sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a2e;
}
.be-faq-modal__close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #fff;
  border: 1px solid #d5d9e0;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  cursor: pointer;
}
.be-faq-modal__body {
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
}
.be-faq-modal__answer >>> p {
  color: #555;
  font-size: 15px;
  line-height: 1.6;
  margin: 0 0 18px;
}
.be-faq-modal__answer >>> p:last-child {
  margin-bottom: 0;
}
.be-faq-modal__answer >>> strong {
  color: #1a1a2e;
  font-weight: 800;
  font-size: 16px;
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/BeFaqSection.vue